import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const POPULAR = [
  "English", "Español", "Français", "Deutsch", "Italiano", "Português", "Polski",
  "Nederlands", "Türkçe", "Русский", "العربية", "中文", "日本語", "한국어", "हिन्दी",
];

export default function TranslateDocument() {
  const { t, i18n } = useTranslation();
  const isES = i18n.language === "es";
  const [file, setFile] = useState(null);
  const [target, setTarget] = useState(isES ? "English" : "Español");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("target_language", target);
      const { data } = await api.post("/translate/document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12" data-testid="translate-page">
      <Link to="/tools/pdf" className="eyebrow text-[#57534E] hover:text-[#1C1917]">← {t("cta_back")}</Link>
      <h1 className="display text-4xl sm:text-6xl mt-3">
        {isES ? "Traducir documento" : "Translate document"}
      </h1>
      <p className="text-[#57534E] mt-3 max-w-2xl font-light">
        {isES
          ? "Sube cualquier archivo (PDF, Word, Excel, PowerPoint, HTML, TXT…) y recibe el mismo formato en el idioma que elijas. La IA conserva celdas, párrafos y diapositivas."
          : "Upload any file (PDF, Word, Excel, PowerPoint, HTML, TXT…) and get the same format in the language you choose. AI keeps cells, paragraphs and slides intact."}
      </p>

      <div className="mt-10">
        {!file ? (
          <DropZone accept="" onFiles={(fs) => setFile(fs[0])} testId="translate-dropzone" />
        ) : (
          <div className="border-2 border-[#1C1917] shadow-[4px_4px_0_0_#1C1917] p-6 bg-white space-y-5" data-testid="translate-file-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <span className="eyebrow">FILE</span>
                <div className="display text-2xl mt-1">{file.name}</div>
                <div className="text-[#57534E] text-sm mt-1">{(file.size / 1024).toFixed(1)} KB</div>
              </div>
              <Button variant="ghost" className="rounded-none hover:bg-[#FFC53D] font-bold border-2 border-[#1C1917]"
                onClick={() => { setFile(null); setResult(null); }}
                data-testid="translate-clear">
                {t("cta_clear")}
              </Button>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 items-end">
              <div className="sm:col-span-1">
                <Label className="eyebrow">{t("ai_target_lang")}</Label>
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger className="border-2 border-[#1C1917] h-11 mt-1 rounded-none font-medium" data-testid="translate-lang-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-[#1C1917]">
                    {POPULAR.map((l) => (
                      <SelectItem key={l} value={l} data-testid={`lang-${l}`}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-1">
                <Label className="eyebrow">{isES ? "Otro idioma" : "Custom"}</Label>
                <Input value={target} onChange={(e) => setTarget(e.target.value)}
                  className="border-2 border-[#1C1917] h-11 mt-1 rounded-none font-medium"
                  data-testid="translate-lang-custom" />
              </div>
              <Button onClick={run} disabled={busy || !target.trim()}
                className="btn-cobalt rounded-none h-11 px-6 sm:col-span-1" data-testid="translate-submit">
                {busy ? t("processing") : (isES ? "Traducir →" : "Translate →")}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-6 border-2 border-[#1C1917] shadow-[6px_6px_0_0_#1C1917] p-6 bg-[#FFC53D]/15 fade-up" data-testid="translate-result">
            <span className="sticker" style={{ background: "#3E63DD", color: "#FFFFFF" }}>
              {isES ? "Traducción lista" : "Translation ready"}
            </span>
            <div className="display text-2xl mt-3">{result.original_filename}</div>
            <div className="text-sm text-[#57534E] mt-1 mono">
              .{result.ext} · {(result.size / 1024).toFixed(1)} KB
            </div>
            <Button onClick={() => downloadFile(result.id, result.original_filename)}
              className="btn-primary rounded-none h-11 px-6 mt-5" data-testid="translate-download">
              ↓ {t("cta_download")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
